import { useTranslation } from 'react-i18next'
import { useTemplateMeals } from '@/features/templates/useTemplateMeals'
import { useAddTemplate } from '@/features/templates/useAddTemplate'
import { TemplateCard } from '@/features/templates/TemplateCard'
import type { OnboardingPreferences } from './types'
import type { TemplateMeal } from '@/features/templates/types'

/** Map cuisine preference labels to template category values. */
const CUISINE_TO_CATEGORIES: Record<string, string[]> = {
  British: ['British', 'Comfort Food', 'Family Favourites'],
  Italian: ['Pasta', 'Italian'],
  Asian: ['Asian', 'Curry', 'Stir-fry'],
  Mexican: ['Mexican'],
  Mediterranean: ['Mediterranean'],
  Other: [],
}

function matchesCuisinePreferences(template: TemplateMeal, cuisines: string[]): boolean {
  if (cuisines.length === 0) return false
  const matchCategories = cuisines.flatMap((c) => CUISINE_TO_CATEGORIES[c] ?? [])
  return matchCategories.some(
    (cat) => template.category.toLowerCase() === cat.toLowerCase(),
  )
}

interface Props {
  preferences: OnboardingPreferences | null
  mealsAddedCount: number
  onMealAdded: () => void
  onContinue: () => void
}

export function OnboardingTemplateStep({ preferences, mealsAddedCount, onMealAdded, onContinue }: Props) {
  const { t } = useTranslation('onboarding')
  const { data: templates, isLoading, isError } = useTemplateMeals()
  const addMutation = useAddTemplate()
  const { t: tTemplates } = useTranslation('templates')

  const handleAdd = (id: string) => {
    addMutation.mutate(id, { onSuccess: () => onMealAdded() })
  }

  const renderTemplateGrid = (items: TemplateMeal[]) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {items.map((template) => (
        <TemplateCard
          key={template.id}
          template={template}
          onAdd={handleAdd}
          isPending={addMutation.isPending && addMutation.variables === template.id}
        />
      ))}
    </div>
  )

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface rounded-xl border border-line p-4 h-[72px] animate-pulse"
            />
          ))}
        </div>
      )
    }

    if (isError) {
      return (
        <p className="text-danger text-sm" role="alert">
          {tTemplates('list.error')}
        </p>
      )
    }

    if (!templates || templates.length === 0) {
      return <p className="text-muted text-sm">{tTemplates('list.empty')}</p>
    }

    // If preferences with cuisines exist, split into recommended / other
    if (preferences && preferences.cuisines.length > 0) {
      const recommended = templates.filter((tmpl) =>
        matchesCuisinePreferences(tmpl, preferences.cuisines),
      )
      const other = templates.filter(
        (tmpl) => !matchesCuisinePreferences(tmpl, preferences.cuisines),
      )

      return (
        <div className="space-y-6">
          {addMutation.isError && (
            <p className="text-danger text-sm" role="alert">
              {tTemplates('list.addError')}
            </p>
          )}
          {recommended.length > 0 && (
            <section aria-label={t('templates.recommendedHeading')}>
              <h2 className="text-sm font-semibold text-accent uppercase tracking-wide mb-2">
                {t('templates.recommendedHeading')}
              </h2>
              {renderTemplateGrid(recommended)}
            </section>
          )}
          {other.length > 0 && (
            <section aria-label={t('templates.otherHeading')}>
              <h2 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
                {t('templates.otherHeading')}
              </h2>
              {renderTemplateGrid(other)}
            </section>
          )}
        </div>
      )
    }

    // No preferences — show full catalogue grouped by category (same as TemplateList)
    const categories = [...new Set(templates.map((tmpl) => tmpl.category))]
    return (
      <div className="space-y-6">
        {addMutation.isError && (
          <p className="text-danger text-sm" role="alert">
            {tTemplates('list.addError')}
          </p>
        )}
        {categories.map((category) => (
          <section key={category} aria-label={category}>
            <h2 className="text-sm font-semibold text-muted uppercase tracking-wide mb-2">
              {category}
            </h2>
            {renderTemplateGrid(templates.filter((tmpl) => tmpl.category === category))}
          </section>
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col flex-1">
      <div className="mb-4">
        <h1 className="text-xl font-bold text-ink">{t('templates.heading')}</h1>
        <p className="text-sm text-muted mt-1">{t('templates.subtitle')}</p>
      </div>
      <div className="flex-1 overflow-y-auto">
        {renderContent()}
      </div>
      <div className="pt-4 pb-2">
        <button
          type="button"
          disabled={mealsAddedCount === 0}
          onClick={onContinue}
          className="w-full rounded-lg bg-accent text-white font-semibold py-2.5 px-4 hover:bg-accent-dark focus:outline-none focus:ring-2 focus:ring-accent transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('templates.continue')}
        </button>
      </div>
    </div>
  )
}
